import React, { ReactNode } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { useTheme } from 'contexts/ThemeContext';

interface AuthPageLayoutProps {
  pageTitle: string;
  children: ReactNode;
}

/**
 * Shared layout for themed auth pages (Login, Set Password / reset-password).
 * Provides full-page background, logo, centered card container, and support block.
 */
export function AuthPageLayout({ pageTitle, children }: AuthPageLayoutProps) {
  const { theme } = useTheme();

  React.useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  React.useEffect(() => {
    if (theme.customer.loginBackgroundUrl) {
      document.body.style.background = `url(${theme.customer.loginBackgroundUrl})`;
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundPosition = 'center';
      document.body.style.backgroundRepeat = 'no-repeat';
      document.body.style.backgroundAttachment = 'fixed';
      document.body.style.overflow = 'hidden';
      document.body.style.margin = '0';
      document.body.style.padding = '0';
    } else if (theme.customer.loginBackgroundColor) {
      document.body.style.background = theme.customer.loginBackgroundColor;
      document.body.style.overflow = 'hidden';
      document.body.style.margin = '0';
      document.body.style.padding = '0';
    }

    return () => {
      document.body.style.background = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundRepeat = '';
      document.body.style.backgroundAttachment = '';
      document.body.style.overflow = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
    };
  }, [theme.customer.loginBackgroundUrl, theme.customer.loginBackgroundColor]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        height: '100%',
        backgroundColor: theme.customer.loginBackgroundColor ?? theme.customer.primaryColor,
        background: theme.customer.loginBackgroundUrl
          ? `url(${theme.customer.loginBackgroundUrl})`
          : theme.customer.loginBackgroundColor
            ? theme.customer.loginBackgroundColor
            : `linear-gradient(135deg, ${theme.customer.primaryColor} 0%, ${theme.customer.primaryColor} 95%, ${theme.customer.primaryColor}f0 100%)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        fontFamily: theme.customer.fontFamily,
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {theme.customer.logo && (
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <img
            src={theme.customer.logo}
            alt="Company Logo"
            style={{
              height: '160px',
              width: 'auto',
              maxWidth: '400px',
              objectFit: 'contain',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          padding: '50px',
          backgroundColor: 'white',
          borderRadius: `${theme.customer.borderRadius}px`,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          width: '100%',
          maxWidth: '400px',
          mx: 'auto',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
          ...(theme.customer.headerBackgroundColor
            ? { borderTop: `4px solid ${theme.customer.primaryColor}` }
            : {}),
        }}
      >
        <Typography
          variant="h4"
          sx={{
            textAlign: 'center',
            mb: 3,
            fontWeight: 'bold',
            color: theme.customer.primaryColor,
            fontFamily: theme.customer.fontFamily || 'inherit',
          }}
        >
          {pageTitle}
        </Typography>

        {children}

        {(theme.customer.supportEmail || theme.customer.id === 'assettech') && (
          <Box sx={{ marginTop: 8, width: '100%' }}>
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: '#666',
                fontFamily: theme.customer.fontFamily || 'inherit',
              }}
            >
              Having Trouble? Reach out to us at{' '}
              <Link
                sx={{
                  color: theme.customer.secondaryColor,
                  textDecoration: 'none',
                  '&:hover': {
                    color: theme.customer.primaryColor,
                    textDecoration: 'underline',
                  },
                  transition: 'color 0.2s ease-in-out',
                }}
                href={`mailto:${theme.customer.supportEmail || 'support@electronicsalpha.com'}`}
              >
                {theme.customer.supportEmail || 'support@electronicsalpha.com'}
              </Link>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
